import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";
import { fPercent } from "src/utils/format-number";
import useLocales from "src/locales/use-locales";

interface PaidShoppingShareTooltipProps {
    lastShoppingVisibilityRate?: number;
    topShoppingVisibilityRate?: number;
    competShoppingVisibilityRate?: number;
    onMouseLeave?: () => void;
}

export default function PaidShoppingShareTooltip({
    lastShoppingVisibilityRate = 0,
    topShoppingVisibilityRate = 0,
    competShoppingVisibilityRate = 0,
    onMouseLeave = () => {},
}: PaidShoppingShareTooltipProps) {
    const theme = useTheme();
    const { t } = useLocales();
    const formatPercent = (value: any) => {
        if (value <= 1) return value + "%";
        const fixed = value.toFixed(0);
        return fixed.endsWith('.0') ? fixed.slice(0, -2) + "%" : fixed + "%";
    };
    
    // Calculate percentages
    // last_shopping_visibility_rate is for 1st position
    // top_shopping_visibility_rate is for 2-3 position
    const firstPercent = Math.min(Math.max(lastShoppingVisibilityRate, 0), 100);
    const topPercent = Math.min(Math.max(topShoppingVisibilityRate, 0), 100);
    const competPercent = Math.min(Math.max(competShoppingVisibilityRate, 0), 100);
    
    // Calculate total for bar visualization
    const total = firstPercent + topPercent + competPercent;
    const firstBarWidth = total > 0 ? (firstPercent / total) * 100 : 0;
    const topBarWidth = total > 0 ? (topPercent / total) * 100 : 0;
    const competBarWidth = total > 0 ? (competPercent / total) * 100 : 0;

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                border: `1px solid rgba(0, 0, 0, 0.12)`,
                borderRadius: '12px',
                overflow: 'hidden',
                minWidth: 250,
            }}
            onMouseLeave={onMouseLeave}
        >
            {/* Header */}
            <Box
                sx={{
                    backgroundColor: alpha("#919EAB", 0.08),
                    px: 2,
                    py: 2,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: 1,
                        color: "#212B36",
                    }}
                >
                    {t("dashboard.nonBrand.columns.paidShoppingShare")}
                </Typography>
            </Box>

            {/* Bar Chart */}
            <Box
                sx={{
                    px: 2,
                    py: 2,
                }}
            >
                <Stack direction="row" spacing={0.5} sx={{ width: '100%', height: 12 }}>
                    {/* 1st position segment (orange) */}
                    {firstBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: firstBarWidth,
                                height: 12,
                                backgroundColor: '#FF9A0B',
                                borderRadius: '2px',
                                minWidth: firstBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                    {/* 2-3 position segment (lighter orange) */}
                    {topBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: topBarWidth,
                                height: 12,
                                backgroundColor: '#FFD89F',
                                borderRadius: '2px',
                                minWidth: topBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                    {/* Competitors segment (grey) */}
                    {competBarWidth > 0 && (
                        <Box
                            sx={{
                                flex: competBarWidth,
                                height: 12,
                                backgroundColor: '#D0D0D0',
                                borderRadius: '2px',
                                minWidth: competBarWidth > 0 ? '2px' : 0,
                            }}
                        />
                    )}
                </Stack>
            </Box>

            {/* Legend */}
            <Box
                sx={{
                    backgroundColor: alpha("#919EAB", 0.05),
                    px: 2,
                    py: 2,
                }}
            >
                <Stack spacing={1.5}>
                    {/* 1st position */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#FF9A0B',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: "#637381",
                                }}
                            >
                                {t("dashboard.nonBrand.positions.first")}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: "#212B36",
                                textAlign: 'right',
                            }}
                        >
                            {formatPercent(firstPercent)}
                        </Typography>
                    </Stack>

                    {/* 2-3 position */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#FFD89F',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: "#637381",
                                }}
                            >
                                {t("dashboard.nonBrand.positions.secondToThird")}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: "#212B36",
                                textAlign: 'right',
                            }}
                        >
                            {formatPercent(topPercent)}
                        </Typography>
                    </Stack>

                    {/* Competitors */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 20,
                                    backgroundColor: '#D0D0D0',
                                    borderRadius: '16px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    color: "#637381",
                                }}
                            >
                                {t("dashboard.nonBrand.competitors")}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: 1,
                                color: "#212B36",
                                textAlign: 'right',
                            }}
                        >
                            {formatPercent(competPercent)}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
}

